import { useRecoilValue } from "recoil";
import React, { useLayoutEffect, useState } from "react";
import { Dimmer, Loader, Modal, Popup } from "semantic-ui-react";
import { Card, Checkbox, Feed, Icon, Label, Rating } from "semantic-ui-react";

import { cardFormat, requestedTrack } from "./atoms";

import { playingTrack } from "./Sequencer";

import {
  formatTime,
  formatTitle,
  isRecent,
  formatSince,
  useIsMobile,
} from "./utils";

import { tracks$ } from "./TracksStream";
import { darkMode } from "./DarkModeToggle";

const formatDuration = (duration, asError) => {
  if (!duration) return asError ? "😥" : "-";

  duration = Math.round(duration);

  return formatTime(duration);
};

const COLOR = { color: "#F07818" };

function SoundIcon() {
  return <Icon name="sound" style={COLOR} />;
}

function PlayingIcon({ track }) {
  const current = useRecoilValue(playingTrack);

  const playing = current.url === track.url;

  useLayoutEffect(() => {
    const id = "card-" + track.id;

    const card = document.getElementById(id);

    const label = card.querySelector(".orange");

    if (label === null) return;

    if (label.style.position === "absolute") return;

    const width = 100;
    const height = 30;

    const cardRect = card.getBoundingClientRect();

    const x = (cardRect.width - width) / 2;
    const y = -14;

    label.style.position = "absolute";
    label.style.left = `${x}px`;
    label.style.top = `${y}px`;
    label.style.width = `${width}px`;
    label.style.height = `${height}px`;
  });

  return playing ? (
    <Label color="orange" circular basic size="large">
      <SoundIcon /> {formatTime(current.position)}
    </Label>
  ) : null;
}

function Timestamps({ track }) {
  const [date, relative] = formatSince(track.lastModified);

  const creationDate = track.creationDate
    ? formatSince(track.creationDate, { dateOnly: true })
    : undefined;
  const reworkDate = track.reworkDate
    ? formatSince(track.reworkDate, { dateOnly: true })
    : undefined;

  return (
    <Card>
      <Card.Content>
        <Feed>
          {date ? (
            <Feed.Event>
              <Icon name="upload" />
              <Feed.Content>
                Updated
                <Feed.Summary>
                  <Feed.Date content={relative} />
                  <Feed.Date content={"/ " + date} />
                </Feed.Summary>
              </Feed.Content>
            </Feed.Event>
          ) : null}
          {reworkDate ? (
            <Feed.Event>
              <Icon name="wrench" />
              <Feed.Content>
                Reworked
                <Feed.Summary>
                  <Feed.Date content={reworkDate[1]} />
                  <Feed.Date content={"/ " + reworkDate[0]} />
                </Feed.Summary>
              </Feed.Content>
            </Feed.Event>
          ) : null}
          {creationDate ? (
            <Feed.Event>
              <Icon name="microphone" />
              <Feed.Content>
                Created
                <Feed.Summary>
                  <Feed.Date content={creationDate[1]} />
                  <Feed.Date content={"/ " + creationDate[0]} />
                </Feed.Summary>
              </Feed.Content>
            </Feed.Event>
          ) : null}
        </Feed>
      </Card.Content>
    </Card>
  );
}

function InfoIcon({ isNew, track }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <i
        className={`${isNew ? "label-icon-new headphones" : "info"
          } icon label-icon`}
        onClick={(ev) => {
          setOpen(true);
          ev.stopPropagation();
        }}
      />
      <Modal
        id="timestamps"
        open={open}
        centered={false}
        dimmer="inverted"
        onClose={() => setOpen(false)}
        content={<Timestamps track={track} />}
      />
    </>
  );
}

function AdditionalInfo({ track }) {
  const isMobile = useIsMobile();
  const isNew = isRecent(track);

  return isMobile ? (
    <InfoIcon isNew={isNew} track={track} />
  ) : (
    <Popup
      content={<Timestamps track={track} />}
      trigger={
        <i
          className={`${isNew ? "label-icon-new headphones" : "info"
            } icon label-icon`}
          onClick={(ev) => {
            ev.stopPropagation();
          }}
        />
      }
      position="left center"
    />
  );
}

function trackProps(track) {
  const duration =
    track.duration || track.error ? (
      formatDuration(track.duration, track.error)
    ) : (
      <Icon loading name="spinner" color="purple" />
    );

  return {
    title: track.title ? track.title : formatTitle(track.url),
    duration,

    enabled: track.enabled,
    titleStyle: track.enabled ? undefined : { color: "lightgray", filter: "blur(2px)" },
  };
}

function CardHeader({ track }) {
  const isDarkMode = useRecoilValue(darkMode);
  const { enabled, title, duration, titleStyle } = trackProps(track);

  return global.features.includeRating ? (
    <Card.Content>
      <Card.Header className={isDarkMode ? "card-header-dark-mode" : ""}>
        <span className="track-title" style={titleStyle}>
          {title}
        </span>
        <Rating
          icon="star"
          maxRating={5}
          rating={track.rating}
          size="mini"
          clearable
          style={{ float: "right" }}
          disabled={!enabled}
          onRate={(_e, { rating }) => tracks$.rate(track, rating)}
        />
      </Card.Header>
      <PlayingIcon track={track} />
      <span style={{ float: "right" }}> {duration}</span>
    </Card.Content>
  ) : (
    <>
      <Card.Content>
        <Card.Header className={isDarkMode ? "card-header-dark-mode" : ""}>
          <span className="track-title" style={titleStyle}>
            {title}
          </span>
          <span style={{ float: "right" }}>{duration}</span>
        </Card.Header>
        <PlayingIcon track={track} />
      </Card.Content>
    </>
  );
}

function SmallCard({ track }) {
  const { title, duration, titleStyle } = trackProps(track);

  const meta = track.authors.join(", ");

  const toggle = () => tracks$.toggle(track);

  return (
    <Popup
      content={meta}
      trigger={
        <Card.Content style={{ display: "flex" }}>
          <Checkbox checked={track.enabled} onChange={toggle} />
          <span style={{ flexGrow: 2, paddingLeft: 5, ...titleStyle }}>
            {title}
          </span>
          <span>{duration}</span>
          <PlayingIcon track={track} />
        </Card.Content>
      }
      mouseEnterDelay={500}
    />
  );
}

export function BasicTrackCard({ track, color, children, disabled }) {
  const isDarkMode = useRecoilValue(darkMode);
  const requestedUrl = useRecoilValue(requestedTrack);
  const format = useRecoilValue(cardFormat);

  const meta = track.authors.join(", ");

  const select = () => tracks$.select(track);
  const toggle = () => tracks$.toggle(track);

  const id = "card-" + track.id;

  const active = track.url === requestedUrl;

  return (
    <Card
      onClick={disabled ? undefined : select}
      id={id}
      color={color}
      className={isDarkMode ? "card-dark-mode" : ""}
    >
      <Dimmer inverted active={active}>
        <Loader />
      </Dimmer>

      {children}

      {format === "small" ? (
        <SmallCard track={track} checked={track.enabled} />
      ) : (
        <>
          <CardHeader track={track} />

          <Card.Content
            meta={
              <>
                <div className="authors-names">{meta}</div>
                <Checkbox
                  style={{ float: "right" }}
                  checked={track.enabled}
                  onChange={toggle}
                  className="track-enable-toggle"
                />
                <AdditionalInfo track={track} />
              </>
            }
          />
        </>
      )}
    </Card>
  );
}
